import React, { useState, memo, useRef, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Accept, useDropzone } from "react-dropzone";
import { ShimmerButton } from "react-shimmer-effects";
import UploadAttachmentSvg from "../../../assets/svg-fc/UploadAttachmentSvg";
import CoreButtons from "../../core-components/button/CoreButtons";
import ErrorText from "../error-text/ErrorText";
import { IAttachmentFile } from "../../../interfaces/corporateEvent";
import CrossIconSvg from "../../../assets/svg-fc/CrossIconSvg";
import {
	ATTACHMENT_FILE_TOO_LARGE_ERROR_TEXT,
	ATTACHMENT_MAX_SIZE_MB,
	ATTACHMENT_UPLOAD_LIMIT_REACHED_ERROR_TEXT,
	FILE_INVALID_TYPE_ERROR_TEXT,
	MAX_ATTACHMENTS,
	IMAGE_UPLOAD_ACCEPTED_FORMATS,
	CORPORATE_QUOTE_CONTENT_ATTACHMENTS_PATH,
	DOCUMENT_PAGE_LIMIT_REACHED_ERROR_TEXT,
} from "../../../constants/formConstants";
import { DATE_FORMAT_SLASH, fDate } from "../../../utils/formatTime";
import {
	checkIfFileIsInvalidType,
	checkIfFileIsTooLarge,
	extractNameAndTimestamp,
	isOverSizedFile,
	getDocumentPageCount,
} from "../../../helpers/common";
import { useToast } from "../../../context/ToasterContext";
import { useAppDispatch } from "../../../hooks/storeHooks";
import { uploadMultipleFiles } from "../../../store/b2b/corporateEvent/corporateEventThunk";
import { getFileIcon, checkIfImage, compressImages } from "../../../utils/commonUtils";
import "./addAndViewAttachments.scss";

interface IProps {
	className?: string;
	attachments: IAttachmentFile[];
	showUploadIcon?: boolean;
	title?: string | React.ReactNode;
	attachmentHelperText?: string | React.ReactNode;
	attachmentTypeText?: string;
	showImportButton?: boolean;
	errorMessage?: string;
	hideUploadAttachmentsSection?: boolean;
	acceptedFormats?: Accept;
	fileSizeLimit?: number;
	maxPages?: number;
	remainingPageLimit?: number;
	maxAttachments?: number;
	multiple?: boolean;
	disabled?: boolean;
	id?: string;
	directoryPath?: string;
	enableCompression?: boolean;
	getPageCount?: (pageCount: number) => void;
	getFiles?: (files: File[]) => void;
	handleSetAttachments: (attachments: IAttachmentFile[]) => void;
}

const AddAndViewAttachments = ({
	className,
	attachments,
	showUploadIcon = true,
	title,
	attachmentHelperText,
	attachmentTypeText,
	showImportButton = true,
	errorMessage,
	hideUploadAttachmentsSection = false,
	acceptedFormats = IMAGE_UPLOAD_ACCEPTED_FORMATS,
	fileSizeLimit = ATTACHMENT_MAX_SIZE_MB,
	maxPages,
	remainingPageLimit,
	maxAttachments = MAX_ATTACHMENTS,
	multiple = true,
	disabled = false,
	id = "",
	directoryPath = CORPORATE_QUOTE_CONTENT_ATTACHMENTS_PATH,
	enableCompression = false,
	getPageCount,
	getFiles,
	handleSetAttachments,
}: IProps) => {
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const attachmentInputRef = useRef<HTMLInputElement | null>(null);
	const [filesLength, setFilesLength] = useState(0);
	const [uploadMultipleFilesLoading, setUploadMultipleFilesLoading] = useState(false);
	const fileTooLargeErrorText = ATTACHMENT_FILE_TOO_LARGE_ERROR_TEXT(fileSizeLimit);
	const uploadLimitErrorText = ATTACHMENT_UPLOAD_LIMIT_REACHED_ERROR_TEXT(maxAttachments);
	const inputId = useMemo(() => `attachmentsInput_${id}`, [id]);

	const onDrop = async (acceptedFiles: File[]) => {
		let files: IAttachmentFile[] = attachments || [];

		try {
			if (isOverSizedFile(acceptedFiles, fileSizeLimit)) {
				return showToast(fileTooLargeErrorText, undefined, "error");
			}

			if (!multiple && maxPages) {
				const pageCount = await getDocumentPageCount(acceptedFiles[0]);
				if (pageCount > (remainingPageLimit as number)) {
					return showToast(DOCUMENT_PAGE_LIMIT_REACHED_ERROR_TEXT(maxPages), undefined, "error");
				}
				getPageCount?.(pageCount);
			}

			getFiles?.(acceptedFiles);
			setFilesLength(acceptedFiles?.length || 0);

			const newLength = (acceptedFiles?.length || 0) + (files?.length || 0);

			if (newLength > maxAttachments) {
				showToast(uploadLimitErrorText, undefined, "error");
				return;
			}

			setUploadMultipleFilesLoading(true);
			const filesToUpload = enableCompression
				? await compressImages(acceptedFiles)
				: acceptedFiles;

			dispatch(
				uploadMultipleFiles({
					files: Array.isArray(filesToUpload) ? filesToUpload : [filesToUpload],
					directory: directoryPath,
				}),
			)
				.unwrap()
				.then((resp) => {
					files = [
						...files,
						...resp.map((item) => ({ fileName: item.fileName || "", fileUrl: item.fileUrl })),
					];
					handleSetAttachments(files);
				})
				.catch((err) => {
					showToast(err.message || "Something went wrong", undefined, "error");
				})
				.finally(() => {
					setUploadMultipleFilesLoading(false);
					setFilesLength(0);
				});
		} catch {
			showToast("Something went wrong", undefined, "error");
		}
	};

	const { getRootProps, getInputProps } = useDropzone({
		onDrop: (acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				onDrop(acceptedFiles);
			}
		},
		accept: acceptedFormats,
		multiple,
		maxSize: fileSizeLimit * 1024 * 1024,
		disabled: disabled || uploadMultipleFilesLoading,
		onDropRejected: (fileRejections) => {
			if (checkIfFileIsTooLarge(fileRejections)) {
				showToast(fileTooLargeErrorText, undefined, "error");
			}
			if (checkIfFileIsInvalidType(fileRejections)) {
				showToast(FILE_INVALID_TYPE_ERROR_TEXT, undefined, "error");
			}
		},
	});

	const handleUploadAttachmentClick = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		const input = document.querySelector(`#${inputId}`);
		if (input instanceof HTMLInputElement) {
			input.click();
			if (attachmentInputRef.current) {
				attachmentInputRef.current.value = "";
			}
		}
	};

	return (
		<>
			<Box className="formTextInputs__attachmentsWrapper">
				{title && (
					<Box className="headingWrapper">
						<Typography className="headingTitle">{title}</Typography>
						<Typography className="headingTitle">{`${attachments.length}/${maxAttachments}`}</Typography>
					</Box>
				)}
				<>
					<Box
						{...getRootProps()}
						className={`attachmentsWrapper ${className} ${uploadMultipleFilesLoading ? "loading" : ""} ${disabled ? "disabled" : ""}`}
						onClick={handleUploadAttachmentClick}
					>
						<input
							id={inputId}
							{...getInputProps()}
							ref={(node) => {
								attachmentInputRef.current = node;
							}}
						/>
						{showUploadIcon && <UploadAttachmentSvg />}
						{attachmentHelperText && (
							<Typography className="attachmentHelperText">{attachmentHelperText}</Typography>
						)}
						{attachmentTypeText && (
							<Typography className="attachmentTypeText">{attachmentTypeText}</Typography>
						)}
						{showImportButton && (
							<CoreButtons
								buttonText="Import"
								className="uploadAttachmentsBtn"
								onclick={(e) => {
									e && handleUploadAttachmentClick(e);
								}}
							/>
						)}
					</Box>
					{errorMessage && <ErrorText errorMessage={errorMessage} />}
				</>
			</Box>

			{!hideUploadAttachmentsSection && (
				<>
					{attachments?.length || filesLength ? (
						<Box className="formTextInputs__uploadedAttachmentsContainer">
							<Typography
								className="headingTitle__bold"
								title={`${attachments.length} file${attachments.length > 1 ? "s" : ""} attached`}
							/>
							<Box className="formTextInputs__uploadedAttachmentsWrapper">
								{attachments.map((attachment) => {
									const isImage = checkIfImage(attachment);

									const fileNameAndTime = extractNameAndTimestamp(attachment.fileName);
									const fileName = fileNameAndTime.name;
									const fileDate = new Date(fileNameAndTime.timestamp)?.toISOString();

									const attachmentImage = isImage ? (
										<img
											src={attachment.fileUrl}
											alt={fileName}
											width={32}
											height={32}
											style={{ borderRadius: "4px" }}
										/>
									) : (
										getFileIcon(attachment)({ className: "uploadedAttachment__icon" })
									);

									return (
										<Box
											className="uploadedAttachment"
											key={attachment.fileName}
											onClick={() => {
												if (attachment.fileUrl) {
													window.open(attachment.fileUrl, "_blank");
												}
											}}
										>
											<Box className="uploadedAttachment__contentWrapper">
												{attachmentImage}
												<Box className="uploadedAttachment__content" sx={{
													width: "85%"
												}}>
													<Typography className="uploadedAttachment__name">{fileName}</Typography>
													{fileDate && (
														<Typography className="uploadedAttachment__date">
															{fDate(fileDate as string, DATE_FORMAT_SLASH)}
														</Typography>
													)}
												</Box>
												<Box
													className="uploadedAttachment__crossIconWrapper"
													onClick={(e) => {
														e.stopPropagation();
														e.preventDefault();
														handleSetAttachments(
															attachments.filter((a) => {
																return a.fileName !== attachment.fileName;
															}),
														);
													}}
												>
													<CrossIconSvg className="uploadedAttachment__crossIcon" />
												</Box>
											</Box>
										</Box>
									);
								})}
								{uploadMultipleFilesLoading &&
									Array.from({ length: filesLength }).map(() => <ShimmerButton size="lg" />)}
							</Box>
						</Box>
					) : null}
				</>
			)}
		</>
	);
};

export default memo(AddAndViewAttachments);
